import { Fragment, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Check, Loader2, Lock, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { RoleBadge } from '@/auth/RoleBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  PAGE_LABELS,
  ROLE_LABELS,
  useRolePermissionsMatrix,
  useSaveRolePermissions,
  type RolePageAccessEntry,
} from '../api';

function entryKey(roleCode: string, pageKey: string) {
  return `${roleCode}|${pageKey}`;
}

/** Visual grouping for the matrix — order matches sidebar nav. */
const PAGE_GROUPS: { title: string; keys: string[] }[] = [
  {
    title: 'Core',
    keys: ['dashboard', 'users', 'admins', 'system-permissions'],
  },
  {
    title: 'Content & safety',
    keys: ['content', 'moderation'],
  },
  {
    title: 'Media & effects',
    keys: [
      'media',
      'effects',
      'media-stickers',
      'media-transitions',
      'effect-packs',
      'tfx-create',
      'tfx-drafts',
      'tfx-review',
      'tfx-published',
    ],
  },
  {
    title: 'Growth & finance',
    keys: ['campaigns', 'finance', 'engagement'],
  },
];

function groupPageKeys(pageKeys: string[]): { title: string; keys: string[] }[] {
  const remaining = new Set(pageKeys);
  const groups: { title: string; keys: string[] }[] = [];

  for (const group of PAGE_GROUPS) {
    const keys = group.keys.filter((k) => remaining.has(k));
    for (const k of keys) remaining.delete(k);
    if (keys.length > 0) groups.push({ title: group.title, keys });
  }

  if (remaining.size > 0) {
    groups.push({ title: 'Other', keys: [...remaining].sort() });
  }

  return groups;
}

function MatrixSkeleton() {
  return (
    <Card className="shadow-none">
      <CardHeader className="space-y-2 border-b border-border/60 pb-4">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-4 w-80 max-w-full" />
      </CardHeader>
      <CardContent className="space-y-3 p-0">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 border-b border-border/40 px-5 py-3 last:border-0">
            <Skeleton className="h-4 w-36" />
            <div className="ml-auto flex flex-1 justify-end gap-8">
              {Array.from({ length: 5 }).map((__, j) => (
                <Skeleton key={j} className="h-4 w-4 rounded-sm" />
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export function SystemPermissionsPage() {
  const query = useRolePermissionsMatrix();
  const save = useSaveRolePermissions();
  const [draft, setDraft] = useState<Map<string, boolean>>(new Map());

  useEffect(() => {
    if (!query.data) return;
    const next = new Map<string, boolean>();
    for (const e of query.data.entries) {
      next.set(entryKey(e.roleCode, e.pageKey), Boolean(e.allowed));
    }
    setDraft(next);
  }, [query.data]);

  const dirty = useMemo(() => {
    if (!query.data) return false;
    for (const e of query.data.entries) {
      const key = entryKey(e.roleCode, e.pageKey);
      if (Boolean(e.allowed) !== Boolean(draft.get(key))) return true;
    }
    return false;
  }, [draft, query.data]);

  const dirtyCount = useMemo(() => {
    if (!query.data) return 0;
    let n = 0;
    for (const e of query.data.entries) {
      const key = entryKey(e.roleCode, e.pageKey);
      if (Boolean(e.allowed) !== Boolean(draft.get(key))) n += 1;
    }
    return n;
  }, [draft, query.data]);

  const pageGroups = useMemo(
    () => (query.data ? groupPageKeys(query.data.pageKeys) : []),
    [query.data],
  );

  const savedAccess = useMemo(() => {
    const map = new Map<string, boolean>();
    if (!query.data) return map;
    for (const e of query.data.entries) {
      map.set(entryKey(e.roleCode, e.pageKey), Boolean(e.allowed));
    }
    return map;
  }, [query.data]);

  const roleGrantedCounts = useMemo(() => {
    if (!query.data) return new Map<string, number>();
    const counts = new Map<string, number>();
    const total = query.data.pageKeys.length;
    for (const roleCode of query.data.roleCodes) {
      if (roleCode === 'administrator') {
        counts.set(roleCode, total);
        continue;
      }
      let granted = 0;
      for (const pageKey of query.data.pageKeys) {
        if (draft.get(entryKey(roleCode, pageKey))) granted += 1;
      }
      counts.set(roleCode, granted);
    }
    return counts;
  }, [draft, query.data]);

  const onToggle = (roleCode: string, pageKey: string, allowed: boolean) => {
    // Administrator is hardcoded full access — not configurable.
    // (Legacy Entra "System Admin" maps to Administrator and is not shown.)
    if (roleCode === 'administrator') {
      toast.error('Administrator always has access to every page (hardcoded).');
      return;
    }
    // System & Permissions page itself is Administrator-only — not matrix-delegable.
    if (pageKey === 'system-permissions') {
      toast.error('System & Permissions can only be managed by Administrator.');
      return;
    }
    setDraft((prev) => {
      const next = new Map(prev);
      next.set(entryKey(roleCode, pageKey), allowed);
      return next;
    });
  };

  const onSave = async () => {
    if (!query.data) return;
    const entries: RolePageAccessEntry[] = query.data.pageKeys.flatMap((pageKey) =>
      query.data.roleCodes.map((roleCode) => ({
        roleCode,
        pageKey,
        allowed: Boolean(draft.get(entryKey(roleCode, pageKey))),
      })),
    );
    try {
      await save.mutateAsync(entries);
      toast.success('Role page access saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save permissions');
    }
  };

  const onReset = () => {
    if (!query.data) return;
    const next = new Map<string, boolean>();
    for (const e of query.data.entries) {
      next.set(entryKey(e.roleCode, e.pageKey), Boolean(e.allowed));
    }
    setDraft(next);
  };

  return (
    <div className={cn('mx-auto w-full max-w-6xl space-y-6 p-6', dirty && 'pb-28')}>
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Shield className="size-5" aria-hidden />
          </div>
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">System & Permissions</h1>
            <p className="max-w-xl text-sm text-muted-foreground">
              Control which Entra roles can open each admin page. Changes apply on the next navigation
              for signed-in users.
            </p>
          </div>
        </div>
        {query.data ? (
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <Badge variant="secondary" className="font-normal">
              {query.data.pageKeys.length} pages
            </Badge>
            <Badge variant="secondary" className="font-normal">
              {query.data.roleCodes.length} roles
            </Badge>
            {dirty ? (
              <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400">
                {dirtyCount} unsaved
              </Badge>
            ) : null}
          </div>
        ) : null}
      </header>

      {query.isLoading ? (
        <MatrixSkeleton />
      ) : query.isError ? (
        <Card className="shadow-none">
          <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
            <div className="flex size-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <AlertCircle className="size-5" aria-hidden />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium">Failed to load role permissions</p>
              <p className="text-sm text-muted-foreground">Check your connection and try again.</p>
            </div>
            <Button variant="outline" onClick={() => void query.refetch()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : !query.data ? (
        <Card className="shadow-none">
          <CardContent className="py-14 text-center text-sm text-muted-foreground">
            No permission matrix available.
          </CardContent>
        </Card>
      ) : (
        <>
          <Card className="overflow-hidden shadow-none">
            <CardHeader className="gap-4 border-b border-border/60 pb-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="space-y-1">
                <CardTitle className="text-base">Page access matrix</CardTitle>
                <CardDescription>
                  Tick a cell to grant that role access to the page. Administrator is always fully
                  unlocked. System & Permissions is Administrator-only.
                </CardDescription>
              </div>
              <div className="flex flex-col items-stretch gap-3 sm:items-end">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="flex size-4 items-center justify-center rounded-sm border border-primary bg-primary text-primary-foreground">
                      <Check className="size-3" aria-hidden />
                    </span>
                    Allowed
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="size-4 rounded-sm border border-primary/40" />
                    Denied
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Lock className="size-3.5 opacity-70" aria-hidden />
                    Locked
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={onReset} disabled={!dirty || save.isPending}>
                    Reset
                  </Button>
                  <Button onClick={() => void onSave()} disabled={!dirty || save.isPending}>
                    {save.isPending ? (
                      <>
                        <Loader2 className="size-4 animate-spin" />
                        Saving…
                      </>
                    ) : (
                      'Save changes'
                    )}
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="sticky left-0 z-20 bg-muted/30 px-5 py-3 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground backdrop-blur-sm">
                        Page
                      </th>
                      {query.data.roleCodes.map((code) => {
                        const granted = roleGrantedCounts.get(code) ?? 0;
                        const total = query.data.pageKeys.length;
                        const locked = code === 'administrator';
                        return (
                          <th key={code} className="px-3 py-3 text-center align-bottom">
                            <div className="flex flex-col items-center gap-1.5">
                              <RoleBadge role={code} />
                              <span className="text-[11px] font-normal tabular-nums text-muted-foreground">
                                {locked ? (
                                  <span className="inline-flex items-center gap-1">
                                    <Lock className="size-3" aria-hidden />
                                    all pages
                                  </span>
                                ) : (
                                  `${granted}/${total}`
                                )}
                              </span>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {pageGroups.map((group) => (
                      <Fragment key={group.title}>
                        <tr className="border-b border-border/60">
                          <td
                            colSpan={1 + query.data.roleCodes.length}
                            className="bg-muted/50 px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                          >
                            {group.title}
                          </td>
                        </tr>
                        {group.keys.map((pageKey) => (
                          <tr
                            key={pageKey}
                            className="group border-b border-border/50 transition-colors last:border-0 hover:bg-muted/25"
                          >
                            <td className="sticky left-0 z-10 bg-card px-5 py-2.5 font-medium group-hover:bg-muted/25">
                              <div className="flex flex-col gap-0.5">
                                <span>{PAGE_LABELS[pageKey] ?? pageKey}</span>
                                {PAGE_LABELS[pageKey] ? (
                                  <span className="font-mono text-[11px] font-normal text-muted-foreground/80">
                                    {pageKey}
                                  </span>
                                ) : null}
                              </div>
                            </td>
                            {query.data.roleCodes.map((roleCode) => {
                              const adminLocked = roleCode === 'administrator';
                              const pageLocked =
                                pageKey === 'system-permissions' && roleCode !== 'administrator';
                              const locked = adminLocked || pageLocked;
                              const key = entryKey(roleCode, pageKey);
                              const checked = adminLocked
                                ? true
                                : pageLocked
                                  ? false
                                  : Boolean(draft.get(key));
                              const cellDirty = !locked && Boolean(savedAccess.get(key)) !== checked;
                              return (
                                <td key={roleCode} className="px-3 py-2.5 text-center">
                                  <div className="flex justify-center">
                                    <label
                                      className={cn(
                                        'relative inline-flex size-8 cursor-pointer items-center justify-center rounded-md transition-colors',
                                        locked
                                          ? 'cursor-not-allowed opacity-60'
                                          : 'hover:bg-muted/80',
                                        cellDirty &&
                                          'ring-1 ring-amber-500/50 ring-offset-1 ring-offset-card',
                                      )}
                                    >
                                      <Checkbox
                                        checked={checked}
                                        disabled={locked}
                                        onCheckedChange={(v) =>
                                          onToggle(roleCode, pageKey, Boolean(v))
                                        }
                                        aria-label={`${ROLE_LABELS[roleCode] ?? roleCode} → ${PAGE_LABELS[pageKey] ?? pageKey}`}
                                      />
                                      {locked ? (
                                        <Lock
                                          className="pointer-events-none absolute -right-0.5 -top-0.5 size-2.5 text-muted-foreground"
                                          aria-hidden
                                        />
                                      ) : null}
                                    </label>
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {dirty ? (
            <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 px-4 py-3 shadow-[0_-8px_30px_rgba(0,0,0,0.25)] backdrop-blur-md sm:inset-x-auto sm:bottom-4 sm:left-1/2 sm:w-[min(72rem,calc(100%-3rem))] sm:-translate-x-1/2 sm:rounded-xl sm:border">
              <div className="flex flex-wrap items-center gap-3">
                <p className="mr-auto text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{dirtyCount}</span> unsaved change
                  {dirtyCount === 1 ? '' : 's'}
                </p>
                <Button variant="outline" onClick={onReset} disabled={save.isPending}>
                  Reset
                </Button>
                <Button onClick={() => void onSave()} disabled={save.isPending}>
                  {save.isPending ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    'Save changes'
                  )}
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
